import { forwardRef, InputHTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';

import SearchIcon from '~/shared/assets/icons/search-default.svg';
import SearchIconSm from '~/shared/assets/icons/magnifying-glass-light-full.svg';

type SearchBarSize = 'sm' | 'md' | 'md-responsive' | 'lg';

interface SearchBarProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: SearchBarSize;
  endAdornment?: ReactNode; // New prop for right-side element like a loader
  inputClassName?: string;
  useSmallSearchIcon?: boolean;
}

const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(
  (
    {
      size = 'md',
      className,
      placeholder = 'Search',
      endAdornment,
      inputClassName,
      useSmallSearchIcon,
      ...props
    },
    ref
  ) => {
    const sizeWrapperClasses: Record<SearchBarSize, string> = {
      sm: 'h-9',
      md: 'h-11',
      'md-responsive': 'h-9 2xl:h-11',
      lg: 'h-[52px]',
    };

    // Define icon size based on component size
    const iconSizeClasses: Record<SearchBarSize, string> = {
      sm: 'size-5',
      md: 'size-6',
      'md-responsive': 'size-5 2xl:size-6',
      lg: 'size-6',
    };

    // Define padding for the input based on size
    const inputPaddingLeftClasses: Record<SearchBarSize, string> = {
      sm: 'pl-10',
      md: 'pl-10',
      'md-responsive': 'pl-10 2xl:pl-11',
      lg: 'pl-11',
    };

    // Add right padding when endAdornment is present
    const inputPaddingRightClasses = endAdornment ? 'pr-10' : 'pr-3';

    // Define input text size based on component size
    const inputTextClasses: Record<SearchBarSize, string> = {
      sm: 'text-sm',
      md: 'text-sm',
      'md-responsive': 'text-sm 2xl:text-base',
      lg: 'text-sm',
    };

    // Define icon positioning based on size
    const iconPositionClasses: Record<SearchBarSize, string> = {
      sm: 'left-3',
      md: 'left-3',
      'md-responsive': 'left-3',
      lg: 'left-3.5',
    };

    return (
      <div className={clsx('relative w-[300px]', sizeWrapperClasses[size], className)}>
        {/* Search Icon SVG - Positioned Absolutely */}
        <div
          className={clsx(
            'absolute top-0 bottom-0 left-0 flex items-center pointer-events-none',
            iconPositionClasses[size] // Adjust left position based on size
          )}
        >
          {useSmallSearchIcon ? (
            <SearchIconSm className={clsx('text-gray-600', iconSizeClasses[size])} />
          ) : (
            <SearchIcon className={clsx('text-gray-600', iconSizeClasses[size])} />
          )}
        </div>

        {/* End Adornment - Positioned Absolutely on the right */}
        {endAdornment && (
          <div className="absolute top-0 bottom-0 right-0 flex items-center pr-3">
            {endAdornment}
          </div>
        )}

        <input
          ref={ref}
          type="text"
          autoComplete="off"
          data-1p-ignore
          data-lpignore="true"
          data-bwignore
          placeholder={placeholder}
          className={clsx(
            // Base styles
            'w-full h-full border rounded-lg bg-white shadowsm transition-colors duration-200 ease-in-out',
            'placeholder:text-primary-900 placeholder:text-sm text-gray-800',
            // Padding: Adjusted for both left and right icons if present
            inputPaddingRightClasses,
            inputPaddingLeftClasses[size],
            inputTextClasses[size],
            'border-gray-300',
            'hover:border-primary-500',
            'focus:border-primary-300 focus:ring-none focus:outline-none',
            // Disabled styles
            'disabled:text-gray-400 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:border-gray-200',
            // ReadOnly styles
            props.readOnly ? 'bg-gray-50 text-gray-500 cursor-default' : '',
            inputClassName
          )}
          {...props}
        />
      </div>
    );
  }
);

SearchBar.displayName = 'SearchBar';
export default SearchBar;
